import { useCallback, useEffect, useRef, useState } from 'react';
import type { DocumentMeta } from '../../core/types';
import { getEngine } from '../../engine/engine';
import {
  commitLiquify,
  LIQUIFY_MODE,
  type LiquifyTool,
} from '../../engine/liquifyOps';
import { commitHistory } from '../../state/actions';
import { RenderTarget } from '../../gl/renderTarget';
import { Modal, Select, SliderRow } from '../components/controls';

const TOOLS: { value: LiquifyTool; label: string }[] = [
  { value: 'push', label: 'Forward Warp' },
  { value: 'twirl', label: 'Twirl Clockwise' },
  { value: 'twirlCCW', label: 'Twirl Counterclockwise' },
  { value: 'pucker', label: 'Pucker' },
  { value: 'bloat', label: 'Bloat' },
  { value: 'freeze', label: 'Freeze' },
  { value: 'thaw', label: 'Thaw' },
  { value: 'reconstruct', label: 'Reconstruct' },
];

/**
 * Self-contained liquify workspace. It owns a private GL canvas so the main
 * editor stays untouched until OK bakes the displacement into the layer.
 */
export function LiquifyDialog({
  doc,
  open,
  onClose,
}: {
  doc: DocumentMeta;
  open: boolean;
  onClose: () => void;
}) {
  const engine = getEngine();
  const viewRef = useRef<HTMLCanvasElement>(null);
  const [tool, setTool] = useState<LiquifyTool>('push');
  const [size, setSize] = useState(64);
  const [pressure, setPressure] = useState(50);
  const [density, setDensity] = useState(50);
  const session = useRef<Session | null>(null);
  const drag = useRef<{ x: number; y: number } | null>(null);

  const redraw = useCallback(() => {
    const current = session.current;
    const canvas = viewRef.current;
    if (!current || !canvas || !engine.available) return;
    const displace = current.displace[current.displaceIndex].texture;
    engine.renderer.applyLiquify(current.preview.framebuffer, current.source.texture, displace);
    const scale = Math.min(1, 520 / Math.max(doc.width, doc.height));
    const width = Math.max(1, Math.round(doc.width * scale));
    const height = Math.max(1, Math.round(doc.height * scale));
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pixels = current.preview.texture.readPixels({
      x: 0,
      y: 0,
      width: doc.width,
      height: doc.height,
    });
    const image = new ImageData(new Uint8ClampedArray(pixels), doc.width, doc.height);
    const offscreen = document.createElement('canvas');
    offscreen.width = doc.width;
    offscreen.height = doc.height;
    offscreen.getContext('2d')!.putImageData(image, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(offscreen, 0, 0, width, height);
  }, [doc.height, doc.width, engine]);

  useEffect(() => {
    if (!open || !engine.available || !doc.activeLayerId) return;
    const resources = engine.resourcesFor(doc);
    const surface = resources.ensureLayer(doc.activeLayerId).surface;
    const source = new RenderTarget(engine.gl, doc.width, doc.height, 'rgba8', 'linear');
    engine.renderer.copy(source.framebuffer, surface.texture);
    const displaceA = new RenderTarget(engine.gl, doc.width, doc.height, 'rgba16f', 'linear');
    const displaceB = new RenderTarget(engine.gl, doc.width, doc.height, 'rgba16f', 'linear');
    const freezeA = new RenderTarget(engine.gl, doc.width, doc.height, 'r8', 'linear');
    const freezeB = new RenderTarget(engine.gl, doc.width, doc.height, 'r8', 'linear');
    const preview = new RenderTarget(engine.gl, doc.width, doc.height, 'rgba8', 'linear');
    displaceA.clear(0, 0, 0, 1);
    displaceB.clear(0, 0, 0, 1);
    freezeA.clear(0, 0, 0, 1);
    freezeB.clear(0, 0, 0, 1);
    session.current = {
      source,
      displace: [displaceA, displaceB],
      displaceIndex: 0,
      freeze: [freezeA, freezeB],
      freezeIndex: 0,
      preview,
    };
    redraw();
    return () => {
      session.current?.source.dispose();
      session.current?.displace[0].dispose();
      session.current?.displace[1].dispose();
      session.current?.freeze[0].dispose();
      session.current?.freeze[1].dispose();
      session.current?.preview.dispose();
      session.current = null;
    };
    // Session is rebuilt only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc.id, doc.activeLayerId, redraw]);

  const toDoc = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * doc.width;
    const y = ((event.clientY - rect.top) / rect.height) * doc.height;
    return { x, y };
  };

  const strokeAt = (point: { x: number; y: number }, previous: { x: number; y: number } | null) => {
    const current = session.current;
    if (!current || !engine.available) return;
    const strength = (pressure / 100) * (density / 100);
    const radius = size / 2;

    if (tool === 'freeze' || tool === 'thaw') {
      const read = current.freeze[current.freezeIndex];
      const write = current.freeze[1 - current.freezeIndex];
      engine.renderer.liquifyFreeze(write.framebuffer, read.texture, {
        centerX: point.x,
        centerY: point.y,
        radius,
        value: tool === 'freeze' ? 1 : 0,
        strength,
        docWidth: doc.width,
        docHeight: doc.height,
      });
      current.freezeIndex = 1 - current.freezeIndex;
      return;
    }

    const read = current.displace[current.displaceIndex];
    const write = current.displace[1 - current.displaceIndex];
    const freeze = current.freeze[current.freezeIndex].texture;
    const delta = previous
      ? { x: point.x - previous.x, y: point.y - previous.y }
      : { x: 0, y: 0 };
    const angle =
      tool === 'twirl' ? 0.35 : tool === 'twirlCCW' ? -0.35 : 0;
    engine.renderer.liquifyBrush(write.framebuffer, read.texture, freeze, {
      mode: LIQUIFY_MODE[tool === 'twirlCCW' ? 'twirlCCW' : tool],
      centerX: point.x,
      centerY: point.y,
      deltaX: delta.x,
      deltaY: delta.y,
      radius,
      strength,
      angle,
      docWidth: doc.width,
      docHeight: doc.height,
    });
    current.displaceIndex = 1 - current.displaceIndex;
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = toDoc(event);
    drag.current = point;
    strokeAt(point, null);
    redraw();
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drag.current) return;
    const point = toDoc(event);
    strokeAt(point, drag.current);
    drag.current = point;
    redraw();
  };

  const onPointerUp = () => {
    drag.current = null;
  };

  const apply = () => {
    const current = session.current;
    if (!current || !doc.activeLayerId) {
      onClose();
      return;
    }
    const displace = current.displace[current.displaceIndex].texture;
    commitHistory(
      doc.id,
      'Liquify',
      commitLiquify(engine, doc, doc.activeLayerId, displace),
    );
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Liquify"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button button--primary" onClick={apply}>
            OK
          </button>
        </>
      }
    >
      <div className="field">
        <span className="field__label">Tool</span>
        <Select value={tool} options={TOOLS} onChange={setTool} />
      </div>
      <SliderRow label="Size" value={size} min={4} max={300} onChange={setSize} />
      <SliderRow label="Pressure" value={pressure} min={1} max={100} onChange={setPressure} />
      <SliderRow label="Density" value={density} min={1} max={100} onChange={setDensity} />
      <p className="dialog__note">Drag on the preview. Freeze protects areas from further warps.</p>
      <canvas
        ref={viewRef}
        className="liquify__view"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      />
    </Modal>
  );
}

interface Session {
  source: RenderTarget;
  displace: [RenderTarget, RenderTarget];
  displaceIndex: number;
  freeze: [RenderTarget, RenderTarget];
  freezeIndex: number;
  preview: RenderTarget;
}
